import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { toast } from "@/common/utils/toast";
import store from "@/store";

import {
  evaluationList,
  evaluationCreate,
} from "@/common/api/rest/evaluation/evaluation";

import type { EvaluationListType } from "@/common/types/evaluation";

import { CockpitTheme } from "@/common/themes";
import { baseConfirm } from "@/common/components";
import {
  evaluationAnalysis,
  evaluationInfos,
  evaluationCompany,
  evaluationCrew,
  evaluationEmployees,
  evaluationEquipment,
  evaluationLaboratory,
  evaluationSamples,
  evaluationReport,
} from "@/components";


export default /*@__PURE__*/_defineComponent({
  __name: '[evaluation]',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const searchInput = ref("");
const statusType = ref("PROGRESS");

const showModalAdd = ref(false);

const evaluations = ref<EvaluationListType[]>([]);

const userType = computed(() => store.getters.type as string);
const accountId = computed(() => route.params.id as string);
const evaluationId = computed(() => route.params.evaluation as string);

const tabList = computed<{ [key: string]: any }>(() => ({
  info: {
    key: "info",
    icon: "evaluate",
    label: t("common.labels.infos"),
    component: evaluationInfos,
  },
  report: {
    key: "report",
    icon: "form",
    label: t("common.labels.reports"),
    component: evaluationReport,
  },
  analysis: {
    key: "analysis",
    icon: "list",
    label: t("common.labels.analysis"),
    component: evaluationAnalysis,
  },
  crew: {
    key: "crew",
    icon: "technical",
    label: t("common.labels.crew"),
    component: evaluationCrew,
  },
  equipment: {
    key: "equipment",
    icon: "equipment",
    label: t("common.labels.equipments"),
    component: evaluationEquipment,
  },
  laboratory: {
    key: "laboratory",
    icon: "laboratory",
    label: t("common.labels.laboratories"),
    component: evaluationLaboratory,
  },
  samples: {
    key: "samples",
    icon: "samples",
    label: t("common.labels.samples"),
    component: evaluationSamples,
  },
  company: {
    key: "company",
    icon: "company",
    label: t("common.labels.company"),
    component: evaluationCompany,
  },
  employees: {
    key: "employees",
    icon: "card",
    label: t("common.labels.employees"),
    component: evaluationEmployees,
  },
}));

const menuList = Object.values(tabList.value)?.reduce(
  (acc: any[], item: any) => {
    acc.push({
      key: item.key,
      icon: item.icon,
      label: item.label,
    });
    return acc;
  },
  [],
);

function checkPermissions() {
  if (
    !userType.value ||
    userType.value === "ADMIN" ||
    userType.value === "MANAGER"
  ) {
    return;
  }

  toast({ message: t("error.unauthorized"), type: "error" });

  router.push(`/${accountId.value}/home`);
}

function fetchEvaluations() {
  if (!accountId.value) return;

  evaluationList(accountId.value, {
    full: true,
    status: statusType.value,
    search: searchInput.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        evaluations.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function inputSearch(value: string) {
  searchInput.value = value;

  if (!value) fetchEvaluations();
}

function search() {
  fetchEvaluations();
}

function refetch() {
  fetchEvaluations();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
}

function addItemConfirm() {
  showModalAdd.value = false;

  evaluationCreate(accountId.value, {
    member_id: store.getters.user.member_id,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        addItemCancel();
        refetch();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeStatus(value: string) {
  statusType.value = value;
  fetchEvaluations();
}

watchEffect(() => {
  checkPermissions();
});

onMounted(() => {
  const status = localStorage.getItem("cockpit-status");
  statusType.value = status || "PROGRESS";

  refetch();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(CockpitTheme), {
      "is-edit": true,
      "account-id": accountId.value,
      "evaluation-id": evaluationId.value,
      evaluations: evaluations.value,
      "tab-list": tabList.value,
      "menu-list": _unref(menuList),
      onUpdateData: _cache[0] || (_cache[0] = ($event: any) => (refetch())),
      onAdd: _cache[1] || (_cache[1] = ($event: any) => (addItem())),
      onInputSearch: inputSearch,
      onSearch: search,
      onChangeStatus: changeStatus
    }, null, 8, ["account-id", "evaluation-id", "evaluations", "tab-list", "menu-list"]),
    _createVNode(_unref(baseConfirm), {
      show: showModalAdd.value,
      title: _unref(t)('evaluation.modal.titles.add_evaluation').toUpperCase(),
      message: _unref(t)('evaluation.modal.messages.add_evaluation'),
      onCancel: _cache[2] || (_cache[2] = ($event: any) => (addItemCancel())),
      onConfirm: _cache[3] || (_cache[3] = ($event: any) => (addItemConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})